import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { siteIntro, siteName } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";

const FORM_ADDR = "https://readdy.ai/api/form/db7ga5v3hiaolq192h20";

export default function About() {
  useSeo({
    title: "关于我们 - 挣扎吧亚当君",
    description:
      "了解挣扎吧亚当君的品牌理念与网站定位：以影视作品介绍与剧情导航为核心，帮助用户深入了解影视内容背后的故事。",
    keywords: "关于挣扎吧亚当君,影视网站,剧情导航,品牌理念",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    data.delete("company_alt");

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") payload.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请检查网络后重试。");
    }
  };

  const values = [
    { icon: "ri-compass-3-line", title: "剧情导航", desc: "把庞杂情节拆成可折叠章节，保留悬念也方便理解。" },
    { icon: "ri-search-eye-line", title: "多维筛选", desc: "按地区、年份、类型、评分复合筛选，快速定位想看的作品。" },
    { icon: "ri-newspaper-line", title: "深度资讯", desc: "行业动态与深度解析并重，了解作品之外的故事。" },
    { icon: "ri-heart-3-line", title: "本地收藏", desc: "收藏记录保存在你自己的设备上，随时回看，隐私无忧。" },
  ];

  const guides = [
    {
      title: "如何使用本站",
      content:
        "通过顶部导航进入电影或电视剧分类，使用左侧/顶部筛选栏细化条件；点击任意卡片进入详情页，即可展开剧情章节、查看相关推荐与关联资讯。",
    },
    {
      title: "关于收藏功能",
      content:
        "收藏基于浏览器本地存储，仅保存在当前设备与浏览器中。清除浏览器数据会同时清空收藏，属于正常现象。",
    },
    {
      title: "隐私政策",
      content:
        "本站为影视文化演示站点，作品、人物、评分与资讯均为虚构示例，不涉及真实个人数据。通过联系表单提交的信息仅用于回复你的留言。",
    },
  ];

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-8 pt-6 md:pt-10">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        <div className="mt-6 max-w-3xl">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-[3px] rounded-full bg-accent-500"></span>
            <span className="text-xs font-label font-medium tracking-[0.2em] text-accent-700 uppercase">
              About Us
            </span>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">
            关于 {siteName}
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">{siteIntro}</p>
        </div>
      </section>

      {/* 品牌理念 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="rounded-lg bg-primary-950 text-background-100 px-6 py-10 md:px-12 md:py-12">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-4">品牌理念</h2>
          <p className="text-sm md:text-base text-background-200/85 leading-loose max-w-3xl">
            名字里的「挣扎」，不是悲观，而是尊重。我们相信每一部值得被记住的作品，都在讲述人在困境中的选择。
            我们的工作，是把这些选择讲清楚——不是替观众下结论，而是提供一张清晰的剧情地图，
            让每个人都有机会读出属于自己的那一层含义。
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v) => (
              <div key={v.title} className="p-5 rounded-lg bg-background-50/5 border border-background-50/10">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500 text-accent-950 mb-3">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="font-heading text-sm font-bold text-background-50">{v.title}</h3>
                <p className="mt-1.5 text-xs text-background-200/75 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* 联系方式与表单 */}
          <div className="bg-background-50 rounded-lg border border-background-200/80 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-2">联系我们</h2>
            <p className="text-sm text-foreground-600 mb-6">
              有想看却没有收录的作品？想聊聊剧情？欢迎给我们留言，我们会认真阅读每一条消息。
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6 text-sm">
              <span className="inline-flex items-center gap-2 text-foreground-700">
                <i className="ri-mail-line text-accent-600"></i>
                hello@adam-struggle.example
              </span>
              <span className="inline-flex items-center gap-2 text-foreground-700">
                <i className="ri-map-pin-line text-accent-600"></i>
                线上运营 · 全球影视爱好者
              </span>
            </div>

            {status === "success" ? (
              <div className="p-6 rounded-lg bg-accent-50 border border-accent-200 text-center">
                <i className="ri-checkbox-circle-line text-3xl text-accent-600"></i>
                <p className="mt-3 font-heading font-bold text-foreground-950">留言已提交，感谢你的支持！</p>
                <p className="mt-1 text-sm text-foreground-600">我们会尽快回复你。</p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-4 text-sm text-primary-600 hover:underline cursor-pointer"
                >
                  再写一条
                </button>
              </div>
            ) : (
              <form id="about-contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你？"
                    className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200/80 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200/80 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言类型
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    required
                    defaultValue=""
                    className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200/80 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none cursor-pointer"
                  >
                    <option value="" disabled>
                      请选择
                    </option>
                    <option value="作品推荐">作品推荐</option>
                    <option value="剧情讨论">剧情讨论</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="合作洽谈">合作洽谈</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="写下你想说的话（最多 500 字）"
                    className="w-full p-4 rounded-md bg-background-100 border border-background-200/80 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none resize-none"
                  ></textarea>
                </div>

                <input
                  className="form-extra-field"
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && (
                  <p className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-4 py-2.5">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {status === "submitting" ? "提交中…" : "提交留言"}
                </button>
              </form>
            )}
          </div>

          {/* 使用说明与隐私 */}
          <div className="space-y-4">
            {guides.map((g) => (
              <div key={g.title} className="bg-background-50 rounded-lg border border-background-200/80 p-6">
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 flex items-center gap-2">
                  <i className="ri-book-open-line text-accent-600"></i>
                  {g.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{g.content}</p>
              </div>
            ))}
            <div className="bg-secondary-100 rounded-lg p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">内容声明</h3>
              <p className="text-sm text-foreground-700 leading-relaxed">
                本站所有影视作品、人物、评分与资讯均为虚构演示内容，仅用于展示网站功能，不构成任何真实评价或推荐。
              </p>
              <Link
                to="/news"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-accent-600 transition-colors cursor-pointer"
              >
                前往资讯频道
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}